import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { loadUser, sectorLogin } from '../../features/auth/authSlice';
import { AppDispatch, RootState } from '../../store';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

/**
 * Login do setor pelo link da paróquia: /acesso/:parishSlug
 * Entra com o nome e a senha criados pela paróquia principal.
 */
const SectorAccessPage: React.FC = () => {
  const { parishSlug = '' } = useParams<{ parishSlug: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state: RootState) => state.auth);
  const [parishName, setParishName] = useState('');
  const [pageError, setPageError] = useState<string | null>(null);
  const [loadingParish, setLoadingParish] = useState(true);
  const [form, setForm] = useState({ name: '', password: '' });

  useEffect(() => {
    let cancelled = false;
    setLoadingParish(true);
    setPageError(null);
    api
      .get(`/auth/sector-access/${encodeURIComponent(parishSlug)}`)
      .then((res) => {
        if (!cancelled) setParishName(res.data?.data?.name || '');
      })
      .catch((err) => {
        if (!cancelled) {
          setPageError(err.response?.data?.message || 'Link de acesso inválido');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingParish(false);
      });
    return () => {
      cancelled = true;
    };
  }, [parishSlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(
      sectorLogin({ slug: parishSlug, name: form.name.trim(), password: form.password })
    );
    if (sectorLogin.fulfilled.match(result)) {
      await dispatch(loadUser());
      navigate('/dashboard');
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4, borderTop: 4, borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AccountTreeIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography component="h1" variant="h5" fontWeight="bold" color="primary">
              Acesso do setor
            </Typography>
          </Box>
          {loadingParish ? (
            <CircularProgress sx={{ my: 3 }} />
          ) : pageError ? (
            <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{pageError}</Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                {parishName ? `${parishName}. ` : ''}
                Entre com o nome e a senha que a paróquia criou para o seu setor.
              </Typography>
              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>
              )}
              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Nome do setor"
                  name="name"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  autoFocus
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Senha"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                />
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading}>
                  {loading ? <CircularProgress size={24} /> : 'Entrar'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default SectorAccessPage;
